import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, Star, Loader2, Quote, MessageSquare, MapPin, RefreshCw, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';

const TIPO_LABELS = {
  residencial: 'Residencial',
  ambulatorio: 'Ambulatorio',
  online: 'Online',
  profesional: 'Profesional individual',
};

const RECIBIO_LABELS = {
  si: 'Sí, todos me contactaron',
  algunas: 'Algunas nomás',
  no: 'Nadie me contactó',
};

const TIEMPO_LABELS = {
  menos_24h: '< 24 h',
  '1_3_dias': '1-3 días',
  mas_3_dias: '> 3 días',
  no_respondieron: 'No respondieron',
};

const FACIL_LABELS = {
  muy_facil: 'Muy fácil',
  facil: 'Fácil',
  regular: 'Regular',
  dificil: 'Difícil',
};

export default function AdminTestimonios() {
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rP, rA, rS] = await Promise.all([
        authAxios.get(`${API}/admin/testimonios?status=pending`, { withCredentials: true }),
        authAxios.get(`${API}/admin/testimonios?status=approved`, { withCredentials: true }),
        authAxios.get(`${API}/admin/testimonios/surveys?status=submitted`, { withCredentials: true }),
      ]);
      setPending(rP.data.testimonios || []);
      setApproved(rA.data.testimonios || []);
      setSurveys(rS.data.surveys || []);
    } catch (e) {
      toast.error('Error cargando testimonios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const approve = async (id) => {
    try {
      await authAxios.post(`${API}/admin/testimonios/${id}/approve`, {}, { withCredentials: true });
      toast.success('Testimonio aprobado y publicado');
      loadAll();
    } catch (e) {
      toast.error('Error aprobando testimonio');
    }
  };

  const reject = async (id) => {
    if (!window.confirm('¿Eliminar definitivamente este testimonio? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.post(`${API}/admin/testimonios/${id}/reject`, {}, { withCredentials: true });
      toast.success('Testimonio eliminado');
      loadAll();
    } catch (e) {
      toast.error('Error eliminando testimonio');
    }
  };

  const toggleFeature = async (id, current) => {
    try {
      await authAxios.post(`${API}/admin/testimonios/${id}/feature?featured=${!current}`, {}, { withCredentials: true });
      toast.success(!current ? 'Testimonio destacado' : 'Se quitó el destacado');
      loadAll();
    } catch (e) {
      toast.error('Error actualizando destacado');
    }
  };

  const stats = useMemo(() => {
    const npsValues = surveys.map((s) => s.nivel_recomendacion).filter((n) => typeof n === 'number');
    const avgNps = npsValues.length ? (npsValues.reduce((a, b) => a + b, 0) / npsValues.length).toFixed(1) : '—';
    const promoters = npsValues.filter((n) => n >= 9).length;
    const detractors = npsValues.filter((n) => n <= 6).length;
    const nps = npsValues.length ? Math.round(((promoters - detractors) / npsValues.length) * 100) : 0;
    const contrataron = surveys.filter((s) => s.centro_contratado).length;
    return {
      total: surveys.length,
      avgNps,
      npsScore: nps,
      contratacionRate: surveys.length ? Math.round((contrataron / surveys.length) * 100) : 0,
    };
  }, [surveys]);

  return (
    <div className="space-y-6" data-testid="admin-testimonios">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={<MessageSquare className="w-5 h-5 text-teal-400" />} label="Encuestas respondidas" value={stats.total} testid="kpi-surveys" />
        <KpiCard icon={<Star className="w-5 h-5 text-amber-400" />} label="NPS promedio (1-10)" value={stats.avgNps} testid="kpi-nps-avg" />
        <KpiCard icon={<TrendingUp className="w-5 h-5 text-emerald-400" />} label="NPS Score" value={`${stats.npsScore}`} testid="kpi-nps-score" />
        <KpiCard icon={<CheckCircle2 className="w-5 h-5 text-blue-400" />} label="Tasa contratación" value={`${stats.contratacionRate}%`} testid="kpi-contrat" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-800">
        <TabBtn label={`Pendientes (${pending.length})`} active={tab === 'pending'} onClick={() => setTab('pending')} testid="tab-pending" color="amber" />
        <TabBtn label={`Aprobados (${approved.length})`} active={tab === 'approved'} onClick={() => setTab('approved')} testid="tab-approved" color="emerald" />
        <TabBtn label={`Encuestas (${surveys.length})`} active={tab === 'surveys'} onClick={() => setTab('surveys')} testid="tab-surveys" color="blue" />
        <div className="ml-auto">
          <Button size="sm" variant="ghost" onClick={loadAll} disabled={loading} className="text-slate-400 hover:text-white" data-testid="btn-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 text-teal-400 animate-spin mx-auto" /></div>
      )}

      {tab === 'pending' && !loading && (
        <div className="space-y-4" data-testid="pending-list">
          {pending.length === 0 ? (
            <EmptyState label="No hay testimonios pendientes de moderación" />
          ) : (
            pending.map((t) => (
              <TestimonioRow
                key={t.testimonio_id}
                t={t}
                actions={
                  <>
                    <Button size="sm" onClick={() => approve(t.testimonio_id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`btn-approve-${t.testimonio_id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(t.testimonio_id)} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`btn-reject-${t.testimonio_id}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  </>
                }
              />
            ))
          )}
        </div>
      )}

      {tab === 'approved' && !loading && (
        <div className="space-y-4" data-testid="approved-list">
          {approved.length === 0 ? (
            <EmptyState label="Aún no has aprobado ningún testimonio" />
          ) : (
            approved.map((t) => (
              <TestimonioRow
                key={t.testimonio_id}
                t={t}
                actions={
                  <>
                    <Button
                      size="sm"
                      onClick={() => toggleFeature(t.testimonio_id, t.featured)}
                      className={t.featured ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : 'bg-slate-800 hover:bg-slate-700 text-amber-400 border border-amber-500/40'}
                      data-testid={`btn-feature-${t.testimonio_id}`}
                    >
                      <Star className={`w-4 h-4 mr-1 ${t.featured ? 'fill-slate-900' : ''}`} />
                      {t.featured ? 'Destacado' : 'Destacar'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(t.testimonio_id)} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid={`btn-remove-${t.testimonio_id}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Quitar
                    </Button>
                  </>
                }
              />
            ))
          )}
        </div>
      )}

      {tab === 'surveys' && !loading && (
        <div className="space-y-4" data-testid="surveys-list">
          {surveys.length === 0 ? (
            <EmptyState label="Aún no hay respuestas de encuestas" />
          ) : (
            surveys.map((s) => (
              <SurveyRow key={s.survey_id} s={s} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, testid }) {
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">{icon}<span className="text-slate-400 text-xs uppercase tracking-wide">{label}</span></div>
        <div className="text-2xl font-bold text-white">{value}</div>
      </CardContent>
    </Card>
  );
}

function TabBtn({ label, active, onClick, testid, color = 'emerald' }) {
  const colorMap = {
    amber: 'text-amber-400 border-amber-400',
    emerald: 'text-emerald-400 border-emerald-400',
    blue: 'text-blue-400 border-blue-400',
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
        active ? colorMap[color] : 'text-slate-400 border-transparent hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
}

function EmptyState({ label }) {
  return (
    <div className="text-center py-14 bg-slate-900/40 border border-dashed border-slate-800 rounded-lg">
      <Quote className="w-8 h-8 text-slate-700 mx-auto mb-3" />
      <p className="text-slate-500 text-sm">{label}</p>
    </div>
  );
}

function TestimonioRow({ t, actions }) {
  const tipos = t.tipos_tratamiento || [];
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-5">
        <div className="flex flex-wrap gap-3 justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-semibold text-sm">{t.nombre_display || 'Anónimo'}</span>
              {t.region && (
                <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                  <MapPin className="w-3 h-3 mr-1" /> {t.region}
                </Badge>
              )}
              {typeof t.nivel_recomendacion === 'number' && (
                <Badge variant="outline" className="border-amber-500/40 text-amber-400 text-xs">
                  <Star className="w-3 h-3 mr-1" /> {t.nivel_recomendacion}/10
                </Badge>
              )}
              {t.featured && (
                <Badge className="bg-amber-500 text-slate-900 text-xs">
                  <Star className="w-3 h-3 mr-1 fill-slate-900" /> Destacado
                </Badge>
              )}
            </div>
            <div className="text-slate-500 text-xs mt-1 flex items-center gap-2">
              <Calendar className="w-3 h-3" /> {t.created_at ? new Date(t.created_at).toLocaleDateString('es-CL') : '—'}
              {t.centro_contratado && <><span>·</span><span>Contrató: <span className="text-slate-300">{t.centro_contratado}</span></span></>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">{actions}</div>
        </div>

        <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-line bg-slate-950/50 border border-slate-800 rounded-lg p-3 mb-3">
          <Quote className="w-4 h-4 text-teal-400 inline-block mr-1 mb-1" />
          {t.testimonio_text}
        </p>

        {tipos.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tipos.map((tp) => (
              <Badge key={tp} variant="outline" className="text-xs border-teal-500/30 text-teal-300">
                {TIPO_LABELS[tp] || tp}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SurveyRow({ s }) {
  const tipos = s.tipos_tratamiento || [];
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-5">
        <div className="flex flex-wrap justify-between items-start mb-3 gap-2">
          <div>
            <div className="text-white font-semibold text-sm">{s.quote_name || 'Anónimo'}</div>
            <div className="text-slate-500 text-xs mt-1 flex items-center gap-2">
              {s.quote_region && <><MapPin className="w-3 h-3" /> {s.quote_region} · </>}
              <Calendar className="w-3 h-3" /> {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString('es-CL') : '—'}
              <span>·</span>
              <span className="text-slate-400">{s.quote_email}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {typeof s.nivel_recomendacion === 'number' && (
              <Badge variant="outline" className={`text-xs ${s.nivel_recomendacion >= 9 ? 'border-emerald-500/40 text-emerald-400' : s.nivel_recomendacion <= 6 ? 'border-red-500/40 text-red-400' : 'border-amber-500/40 text-amber-400'}`}>
                <Star className="w-3 h-3 mr-1" /> NPS {s.nivel_recomendacion}/10
              </Badge>
            )}
            {s.publicar_testimonio && (
              <Badge className="bg-teal-500/20 text-teal-300 border border-teal-500/40 text-xs">Autoriza publicar</Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs mb-3">
          <MetaRow label="Recibió cotizaciones" value={RECIBIO_LABELS[s.recibio_cotizaciones] || s.recibio_cotizaciones || '—'} />
          <MetaRow label="Tiempo respuesta" value={TIEMPO_LABELS[s.tiempo_respuesta_centros] || '—'} />
          <MetaRow label="Facilidad" value={FACIL_LABELS[s.proceso_facil] || '—'} />
          <MetaRow label="Contrató" value={s.centro_contratado || '—'} />
        </div>

        {tipos.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {tipos.map((tp) => (
              <Badge key={tp} variant="outline" className="text-xs border-teal-500/30 text-teal-300">{TIPO_LABELS[tp] || tp}</Badge>
            ))}
          </div>
        )}

        {s.recomendacion && (
          <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-3 mb-2">
            <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Opinión</div>
            <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-line">{s.recomendacion}</p>
          </div>
        )}
        {s.mejora_sugerencia && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
            <div className="text-amber-400 text-xs uppercase tracking-wide mb-1">Qué mejorarían</div>
            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">{s.mejora_sugerencia}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="bg-slate-950/50 border border-slate-800 rounded p-2">
      <div className="text-slate-500 uppercase tracking-wide text-[10px]">{label}</div>
      <div className="text-slate-200 mt-0.5 truncate">{value}</div>
    </div>
  );
}
